import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import UnifiedDatePicker from "../../components/UnifiedDatePicker";
import { toast } from "react-hot-toast";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faIdCard,
  faTag,
  faCalendarAlt,
  faGraduationCap,
  faBuildingColumns,
  faBriefcase,
  faFloppyDisk,
  faUserClock,
} from "@fortawesome/free-solid-svg-icons";
import Can from "../../permissions/Can";

export default function EditHalfEmployee() {
  const { emp_id } = useParams();
  const navigate = useNavigate();
  const { register, handleSubmit, setValue, watch } = useForm();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [employeeInfo, setEmployeeInfo] = useState(null);

  const schoolsData = useSelector((state) => state.main.data?.schoolData || []);

  const schoolNameOf = (rgmw) =>
    schoolsData.find((s) => String(s.rgmw) === String(rgmw))?.schoolname || "غير معروف";

  const schoolOptions = useMemo(
    () =>
      schoolsData
        .filter((s) => s.rgmw)
        .map((s) => ({ value: String(s.rgmw), label: s.schoolname })),
    [schoolsData]
  );

  // تحميل بيانات ربط النصف مركز من الجدول الجديد
  useEffect(() => {
    const load = async () => {
      try {
        const credentials = JSON.parse(localStorage.getItem("userCredentials"));
        const token = credentials?.token;
        const res = await axios.get(
          `https://api.alfares.cloud/api/alfares/getHalfEmployee/${emp_id}/${token}`
        );
        const d = res.data || {};
        setEmployeeInfo({
          emp_name: d.emp_name || "",
          emp_id: d.emp_id || emp_id,
          current_rgmw: d.current_rgmw,
        });

        const formatDate = (val) => {
          if (!val || String(val).startsWith("1899")) return "";
          const dt = new Date(val);
          return isNaN(dt.getTime()) ? "" : dt.toISOString().split("T")[0];
        };

        setValue("shared_rgmw", d.shared_rgmw ? String(d.shared_rgmw) : "");
        setValue("job_senf", d.job_senf || "");
        setValue("qualification1", d.qualification1 || "");
        setValue("specialization", d.specialization || "");
        setValue("university", d.university || "");
        setValue("graduation_year", d.graduation_year || "");
        setValue("hiring_date", formatDate(d.hiring_date));
        setValue("current_job_date", formatDate(d.current_job_date));
      } catch (err) {
        console.error("Error loading half-markaz link:", err);
        setError("لم يتم العثور على بيانات ربط النصف مركز");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [emp_id, setValue]);

  const onSubmit = async (data) => {
    try {
      const credentials = JSON.parse(localStorage.getItem("userCredentials"));
      const token = credentials?.token;
      if (!token) {
        toast.error("التوكن مفقود");
        return;
      }

      if (!data.shared_rgmw) {
        toast.error("اختر المدرسة المشتركة");
        return;
      }

      const emptyToNull = (val) =>
        typeof val === "string" && val.trim() === "" ? null : val;

      const formatDate = (val) => {
        if (!val || val === "") return null;
        const d = new Date(val);
        return isNaN(d.getTime()) ? null : d.toISOString().split("T")[0];
      };

      // shared_rgmw = المدرسة المشتركة الجديدة (تُحفظ في السجل الأساسي)
      const payload = {
        shared_rgmw: data.shared_rgmw,
        job_senf: emptyToNull(data.job_senf),
        qualification1: emptyToNull(data.qualification1),
        specialization: emptyToNull(data.specialization),
        university: emptyToNull(data.university),
        graduation_year: emptyToNull(data.graduation_year),
        hiring_date: formatDate(data.hiring_date),
        current_job_date: formatDate(data.current_job_date),
      };

      await axios.put(
        `https://api.alfares.cloud/api/alfares/updateHalfEmployee/${emp_id}/${token}`,
        payload
      );

      toast.success("تم حفظ التعديلات بنجاح");
      navigate(-1);
    } catch (error) {
      console.error("Error updating half-markaz link:", error);
      toast.error("فشل في حفظ التعديلات");
    }
  };

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      height: "44px",
      minHeight: "44px",
      background: "var(--panel-2)",
      borderColor: state.isFocused ? "var(--accent)" : "var(--border)",
      borderRadius: "6px",
      color: "var(--text)",
      fontFamily: "Cairo",
      boxShadow: state.isFocused ? "0 0 0 1px var(--accent)" : "none",
      display: "flex",
      alignItems: "center",
      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      "&:hover": {
        borderColor: state.isFocused ? "var(--accent)" : "var(--muted)",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      display: "flex",
      alignItems: "center",
      height: "44px",
      padding: "0 12px",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({
      ...base,
      background: "var(--panel)",
      border: "1px solid var(--border)",
      borderRadius: "6px",
      overflow: "hidden",
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    }),
    option: (base, state) => ({
      ...base,
      background: state.isSelected
        ? "var(--accent)"
        : state.isFocused
        ? "color-mix(in srgb, var(--accent) 8%, transparent)"
        : "transparent",
      color: state.isSelected ? "#fff" : "var(--text)",
      cursor: "pointer",
      fontSize: "14px",
      fontFamily: "Cairo",
      textAlign: "right",
      "&:active": { background: "var(--accent)" },
    }),
    singleValue: (base) => ({
      ...base,
      color: "var(--text)",
      fontFamily: "Cairo",
      textAlign: "right",
      margin: 0,
      padding: 0,
    }),
    placeholder: (base) => ({
      ...base,
      color: "var(--muted)",
      fontSize: "14px",
      textAlign: "right",
      margin: 0,
      padding: 0,
    }),
    input: (base) => ({
      ...base,
      color: "var(--text)",
      textAlign: "right",
      margin: 0,
      padding: 0,
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "var(--muted)",
      padding: "0 12px",
      "&:hover": { color: "var(--accent)" },
    }),
  };

  if (loading) {
    return (
      <div className="half-edit-wrapper anim-fade-in">
        <div className="premium-edit-container">
          <div className="premium-card" style={{ textAlign: "center", padding: "60px 20px" }}>
            <div className="premium-loader" style={{ margin: "0 auto 12px" }}></div>
            <span style={{ color: "var(--muted)" }}>جارٍ تحميل البيانات...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="half-edit-wrapper anim-fade-in">
        <div className="premium-edit-container">
          <div className="premium-card" style={{ textAlign: "center", padding: "60px 20px" }}>
            <div style={{ color: "#d32f2f", fontSize: "16px", marginBottom: "20px" }}>
              {error}
            </div>
            <button onClick={() => navigate(-1)} className="premium-submit-btn" style={{ width: "auto", padding: "10px 24px" }}>
              العودة للصفحة السابقة
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="half-edit-wrapper anim-fade-in">
      <div className="premium-edit-container">
        {/* Header */}
        <div className="premium-header-modern">
          <div className="header-left">
            <div className="header-icon-box">
              <FontAwesomeIcon icon={faUserClock} />
            </div>
            <div className="header-text-box">
              <h2>تعديل النصف مركز</h2>
              <div className="breadcrumb-info">
                <span>نظام الفارس</span>
                <span className="separator">/</span>
                <span className="current-page">{schoolNameOf(employeeInfo?.current_rgmw)}</span>
                <span className="separator">|</span>
                <span style={{ color: "var(--accent)" }}>{employeeInfo?.emp_name || ""}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="premium-card page-card">
          <form onSubmit={handleSubmit(onSubmit)} className="premium-form">
            {/* بيانات الربط */}
            <div className="form-section">
              <h5 className="section-title">
                <FontAwesomeIcon icon={faUser} />
                بيانات ربط النصف مركز
              </h5>
              <div className="section-grid">
                <div className="input-field">
                  <label>اسم الموظف</label>
                  <div className="input-wrapper">
                    <FontAwesomeIcon icon={faUser} className="field-icon" />
                    <input value={employeeInfo?.emp_name || ""} disabled placeholder="اسم الموظف" />
                  </div>
                </div>

                <div className="input-field">
                  <label>رقم الهوية</label>
                  <div className="input-wrapper">
                    <FontAwesomeIcon icon={faIdCard} className="field-icon" />
                    <input value={employeeInfo?.emp_id || ""} disabled placeholder="رقم الهوية" />
                  </div>
                </div>

                <div className="input-field">
                  <label>المدرسة الحالية</label>
                  <div className="input-wrapper">
                    <FontAwesomeIcon icon={faBuildingColumns} className="field-icon" />
                    <input
                      value={schoolNameOf(employeeInfo?.current_rgmw)}
                      disabled
                      placeholder="المدرسة الحالية"
                    />
                  </div>
                </div>

                <div className="input-field">
                  <label>المدرسة المشتركة</label>
                  <Select
                    options={schoolOptions}
                    value={schoolOptions.find((opt) => opt.value === String(watch("shared_rgmw") || "")) || null}
                    onChange={(selected) => setValue("shared_rgmw", selected?.value || "")}
                    placeholder="اختر المدرسة المشتركة"
                    styles={customSelectStyles}
                    isRtl={true}
                    isSearchable={true}
                    menuPortalTarget={document.body}
                  />
                </div>
              </div>
            </div>

            {/* المؤهلات والوظيفة */}
            <div className="form-section">
              <h5 className="section-title">
                <FontAwesomeIcon icon={faGraduationCap} />
                المؤهلات العلمية والوظيفة
              </h5>
              <div className="section-grid">
                <div className="input-field">
                  <label>تصنيف الوظيفة</label>
                  <div className="input-wrapper">
                    <FontAwesomeIcon icon={faBriefcase} className="field-icon" />
                    <input {...register("job_senf")} placeholder="تصنيف الوظيفة" />
                  </div>
                </div>

                <div className="input-field">
                  <label>المؤهل العلمي</label>
                  <div className="input-wrapper">
                    <FontAwesomeIcon icon={faGraduationCap} className="field-icon" />
                    <input {...register("qualification1")} placeholder="مثلاً: بكالوريوس" />
                  </div>
                </div>

                <div className="input-field">
                  <label>التخصص</label>
                  <div className="input-wrapper">
                    <FontAwesomeIcon icon={faTag} className="field-icon" />
                    <input {...register("specialization")} placeholder="التخصص الدقيق" />
                  </div>
                </div>

                <div className="input-field">
                  <label>الجامعة / الكلية</label>
                  <div className="input-wrapper">
                    <FontAwesomeIcon icon={faBuildingColumns} className="field-icon" />
                    <input {...register("university")} placeholder="اسم الجامعة" />
                  </div>
                </div>

                <div className="input-field">
                  <label>سنة التخرج</label>
                  <div className="input-wrapper">
                    <FontAwesomeIcon icon={faCalendarAlt} className="field-icon" />
                    <input {...register("graduation_year")} placeholder="YYYY" inputMode="numeric" />
                  </div>
                </div>

                <div className="input-field">
                  <label>تاريخ التعيين</label>
                  <UnifiedDatePicker
                    value={watch("hiring_date")}
                    onChange={(date) => {
                      const event = { target: { value: date } };
                      register("hiring_date").onChange(event);
                    }}
                    placeholder="تاريخ التعيين"
                  />
                </div>

                <div className="input-field">
                  <label>تاريخ الوظيفة الحالية</label>
                  <UnifiedDatePicker
                    value={watch("current_job_date")}
                    onChange={(date) => {
                      const event = { target: { value: date } };
                      register("current_job_date").onChange(event);
                    }}
                    placeholder="تاريخ الوظيفة الحالية"
                  />
                </div>
              </div>
            </div>

            {/* حفظ */}
            <div className="form-actions">
              <Can module="employees" action="edit">
                <button type="submit" className="premium-submit-btn">
                  <div className="btn-content">
                    <FontAwesomeIcon icon={faFloppyDisk} />
                    <span>حفظ التعديلات</span>
                  </div>
                </button>
              </Can>
            </div>
          </form>
        </div>
      </div>

      <style>{`
        .half-edit-wrapper {
          padding: 5px;
          color: var(--text);
          font-family: 'Cairo', sans-serif;
        }

        .half-edit-wrapper .premium-edit-container {
          width: 100%;
          max-width: 900px;
          margin: 0 auto;
        }

        .half-edit-wrapper .premium-header-modern {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: var(--panel);
          border: 1px solid var(--border);
          border-radius: 6px;
          padding: 16px 20px;
          margin-bottom: 16px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }

        .half-edit-wrapper .header-left {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }

        .half-edit-wrapper .header-icon-box {
          width: 40px;
          height: 40px;
          border-radius: 6px;
          background: var(--accent);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          color: #fff;
          flex-shrink: 0;
        }

        .half-edit-wrapper .header-text-box h2 {
          margin: 0;
          font-size: 17px;
          font-weight: 400;
          font-family: 'Cairo', sans-serif;
        }

        .half-edit-wrapper .breadcrumb-info {
          display: flex;
          align-items: center;
          gap: 4px;
          color: var(--muted);
          font-size: 12px;
          margin-top: 2px;
          flex-wrap: wrap;
        }

        .half-edit-wrapper .separator { margin: 0 4px; }
        .half-edit-wrapper .current-page { color: var(--accent); }

        .half-edit-wrapper .premium-card {
          background: var(--panel);
          border: 1px solid var(--border);
          border-radius: 6px;
          padding: 24px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }

        .half-edit-wrapper .form-section {
          margin-bottom: 24px;
          padding-bottom: 24px;
          border-bottom: 1px solid var(--border);
        }

        .half-edit-wrapper .form-section:last-of-type {
          margin-bottom: 0;
          padding-bottom: 0;
          border-bottom: none;
        }

        .half-edit-wrapper .section-title {
          font-size: 15px;
          font-weight: 400;
          color: var(--text);
          margin: 0 0 16px 0;
          display: flex;
          align-items: center;
          gap: 8px;
          font-family: 'Cairo', sans-serif;
        }

        .half-edit-wrapper .section-title svg { color: var(--accent); }

        .half-edit-wrapper .section-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        .half-edit-wrapper .span-2 { grid-column: span 2; }

        .half-edit-wrapper .input-field label {
          display: block;
          margin-bottom: 6px;
          font-size: 13px;
          color: var(--muted);
          font-weight: 400;
          font-family: 'Cairo', sans-serif;
        }

        .half-edit-wrapper .input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .half-edit-wrapper .field-icon {
          position: absolute;
          right: 12px;
          color: var(--muted);
          font-size: 14px;
          z-index: 1;
          pointer-events: none;
        }

        .half-edit-wrapper .input-wrapper input {
          width: 100%;
          height: 44px;
          background: var(--panel-2);
          border: 1px solid var(--border);
          border-radius: 6px;
          padding: 0 38px 0 12px;
          color: var(--text);
          font-size: 14px;
          font-family: 'Cairo', sans-serif;
          transition: border-color 0.2s, box-shadow 0.2s;
          box-sizing: border-box;
        }

        .half-edit-wrapper .input-wrapper input:focus {
          border-color: var(--accent);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 12%, transparent);
          outline: none;
          background: var(--panel);
        }

        .half-edit-wrapper .input-wrapper input:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .half-edit-wrapper .form-actions {
          display: flex;
          justify-content: flex-end;
          margin-top: 24px;
        }

        .half-edit-wrapper .premium-submit-btn {
          background: var(--accent);
          border: none;
          border-radius: 6px;
          padding: 12px 32px;
          color: #fff;
          font-size: 15px;
          font-family: 'Cairo', sans-serif;
          cursor: pointer;
          transition: background 0.2s;
        }

        .half-edit-wrapper .premium-submit-btn:hover {
          background: #1d7567;
        }

        .half-edit-wrapper .btn-content {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }

        @media (max-width: 768px) {
          .half-edit-wrapper { padding: 0; }
          .half-edit-wrapper .premium-header-modern {
            padding: 14px 12px;
            border-radius: 6px;
            margin-bottom: 12px;
          }
          .half-edit-wrapper .header-text-box h2 { font-size: 16px; }
          .half-edit-wrapper .breadcrumb-info { font-size: 11px; }
          .half-edit-wrapper .premium-card { padding: 16px 12px; }
          .half-edit-wrapper .section-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }
          .half-edit-wrapper .span-2 { grid-column: span 1; }
          .half-edit-wrapper .form-section {
            margin-bottom: 18px;
            padding-bottom: 18px;
          }
          .half-edit-wrapper .form-actions { margin-top: 18px; }
          .half-edit-wrapper .premium-submit-btn {
            width: 100%;
            padding: 14px;
          }
        }
      `}</style>
    </div>
  );
}
